import {
  IonButton,
  IonCol,
  IonGrid,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonRow,
  IonSelect,
  IonSelectOption,
} from "@ionic/react";
import dayjs from "dayjs";
import { calendar, peopleOutline } from "ionicons/icons";
import React, { useEffect, useState } from "react";
import "../../../pages/StatusBoard/components/StatusBoardTable/components/ExpandedCard/components/AddEditPatient/AddEditPatient.scss";
import SelectCaseType from "../SelectCaseTypes/SelectCaseType";
import { statusBoardService } from "../../../pages/StatusBoard/StatusBoard.service";
import {
  PatientSearchWithOutMRNParms,
  PatientSearchWithMRNParms,
} from "../../../pages/StatusBoard/StatusBoard.model";
import DatePickerComponent from "../DatePicker/datepicker-component";
import { useTranslation } from "react-i18next";

interface EpicPatientDetailsFormProps {
  onCancel: () => void;
  isOpen: boolean;
  handleSaveButton: any;
  title: string;
  selectedProvidersNames?: any;
  patientCaseTypes?: any;
  getSelectedProvidersList?: any;
  isSelectCaseType: boolean;
  rowData: any;
  isEMTUser: boolean;
}

export const EpicPatientDetailsForm: React.FC<EpicPatientDetailsFormProps> = ({
  onCancel,
  isOpen,
  handleSaveButton,
  title,
  selectedProvidersNames,
  patientCaseTypes,
  getSelectedProvidersList,
  isSelectCaseType,
  rowData,
  isEMTUser,
}) => {
  const {t} = useTranslation("statusBoard");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [searchID, setSearchID] = useState("");
  const [isCalendarModalOpen, setIsCalendarModalOpen] =
    useState<boolean>(false);
  const [isFormCalendarModalOpen, setIsFormCalendarModalOpen] =
    useState<boolean>(false);
  const [isShowResult, setIsShowResult] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [isErrorID, setIsErrorID] = useState<boolean>(false);
  const [showSelectProviderModal, setShowSelectProviderModal]: any =
    useState(false);
  const [resultPatientsList, setResultPatientsList] = useState<any>([]);
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    dob: "",
    mrn: "",
    gender: "",
  });
  const [isEPICSearch, setIsEPICSearch] = useState<boolean>(false);
  const [fetchedResultData, setFetchedResultData] = useState<any>();
  const [isResultEmpty, setIsResultEmpty] = useState<boolean>(false);
  const [selectedSearchDOB, setSelectedSearchDOB] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [customCaseTypeName, setCustomCaseTypeName] = useState<string>("");

  const handleResultItemClick = (selectedPatient: any) => {
    // setting the selected patient data into a state
    // To use this value in AddEditPatient Component
    setFetchedResultData(selectedPatient);
    // Filling the form with the fetched data
    setFormData({
      ...formData,
      firstName: selectedPatient?.first_name ?? "",
      middleName: selectedPatient?.middle_name ?? "",
      lastName: selectedPatient?.last_name ?? "",
      dob: selectedPatient?.dob ?? "",
      mrn: selectedPatient?.mr_number
        ? selectedPatient?.mr_number
        : selectedPatient?.ehr_id,
      gender: selectedPatient?.gender,
    });
    // To show result lists
    setIsShowResult(false);
  };

  const clearFields = () => {
    setFormData({
      ...formData,
      firstName: "",
      middleName: "",
      lastName: "",
      dob: "",
      mrn: "",
      gender: "",
    });
  };

  const handlePatientSearch = async (isEPICSearch: boolean) => {
    setIsError(firstName && lastName && dob ? false : true);
    setIsEPICSearch(isEPICSearch);
    const payload: PatientSearchWithOutMRNParms = {
      firstName,
      lastName,
      dateOfBirth: dob,
      epicSearch: isEPICSearch,
    };
    if (firstName && lastName && dob) {
      // setting mr id input to empty
      setSearchID("");
      setIsLoading(true);
      const result = await statusBoardService.patientSearchWithOutMRN(payload);
      // Setting the result array into a state
      // To map the  result list
      setResultPatientsList(result?.patientData);
      setIsResultEmpty(result?.patientData?.length === 0);
      setIsShowResult(true);
      if (!rowData) {
        clearFields();
      }
      setIsLoading(false);
    }
  };

  const handlePatientSearchWithID = async () => {
    setIsErrorID(searchID ? false : true);
    setIsEPICSearch(false);
    const payload: PatientSearchWithMRNParms = {
      mrn: searchID,
    };
    if (searchID) {
      setFirstName("");
      setLastName("");
      setDob("");
      setIsLoading(true);
      const result = await statusBoardService.patientSearchWithMRN(payload);
      setResultPatientsList(result?.patientData);
      setIsResultEmpty(result?.patientData?.length === 0);
      setIsShowResult(true);
      if (!rowData) {
        clearFields();
      }
      setIsLoading(false);
    }
  };

  const isDataEmpty = () => {
    const { dob, firstName, middleName, lastName, gender, mrn } = formData;
    return isEMTUser
      ? !(dob && firstName && gender)
      : !(dob && firstName && gender && mrn);
  };

  // filling the form value when it is in edit mode
  useEffect(() => {
    if (rowData) {
      setFormData({
        ...formData,
        firstName: rowData?.first_name ?? "",
        middleName: rowData?.middle_name ?? "",
        lastName: rowData?.last_name ?? "",
        dob: rowData?.dob ?? "",
        mrn: rowData?.mr_number ?? "",
        gender: rowData?.gender,
      });
      setFetchedResultData(rowData);
    }
  }, [rowData]);

  useEffect(() => {
    if (!formData.firstName && !formData.gender) {
      setFormData({
        ...formData,
        firstName: "",
        middleName: "",
        lastName: "",
        dob: "",
        mrn: "",
        gender: "",
      });
    } else {
      setFormData(formData);
    }
  }, [formData.gender]);

  const isEHIRAccount = () => {
    return fetchedResultData?.ehr_id ? true : false;
  };

  // for restricting number and space in name input field
  const handleKeyPress = (e: any) => {
    const charCode = e.charCode;
    // Check if the character is a number or space
    if ((charCode >= 48 && charCode <= 57) || charCode === 32) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (rowData?.id) {
      const otherCaseType = rowData?.patient_case_types?.find(
        (type: any) => type?.id === 0
      );
      setCustomCaseTypeName(otherCaseType?.case);
    }
  }, [rowData]);

  return (
    <div>
      <IonModal
        isOpen={isOpen}
        backdropDismiss={false}
        className="add-edit-patient-popup"
      >
        <div className="edit-patient-cont">
          <div className="header-text">
            <span className="title">{title}</span>
            <div
              className="icon-close "
              onClick={() => {
                onCancel();
                clearFields();
                setIsEPICSearch(false);
              }}
            ></div>
          </div>
          <div className="search-patient-container ">
            <div
              className="search-and-casetype-row"
              style={{
                justifyContent: isEMTUser ? "flex-end" : "space-between",
              }}
            >
              {!isEMTUser && <p>{t("search")}</p>}
              {/* Select case type section */}
              {isSelectCaseType && (
                <div
                  style={{
                    flexDirection: "row",
                    display: "flex",
                    alignItems: "flex-end",
                  }}
                >
                  <div
                    className="patient-details-form-input-container"
                    style={{
                      flexDirection: "column",
                      display: "flex",
                      alignItems: "flex-start",
                      margin: "0 0 .25rem 0",
                    }}
                  >
                    <p style={{ width: "40%" }}>Case types</p>
                    <IonItem className="select-cs-wrapper">
                      <IonInput
                        readonly
                        className="schedule-input"
                        onClick={() => setShowSelectProviderModal(true)}
                        value={
                          selectedProvidersNames[0] === "ALL" ||
                          selectedProvidersNames.length === 0
                            ? "Select case types"
                            : selectedProvidersNames.join()
                        }
                      ></IonInput>
                    </IonItem>

                    <SelectCaseType
                      showModal={showSelectProviderModal}
                      onOk={() => {
                        setShowSelectProviderModal(false);
                      }}
                      ListData={patientCaseTypes}
                      isAllSelected={true}
                      onCancel={() => {
                        setShowSelectProviderModal(false);
                      }}
                      getSelectedProviders={getSelectedProvidersList}
                      isFromCaseHistoryEdit={true}
                    />
                  </div>
                  {selectedProvidersNames.find(
                    (name: string) => name === "Other"
                  ) && (
                    <div
                      className="col-lg-6 col-md-8 col-sm-12"
                      style={{ margin: "0 0 .25rem 0" }}
                    >
                      <>
                        <label className="ems-form-label">
                        {t("otherCaseType")}
                        </label>

                        <input
                          type="text"
                          className="form-control custom-type emt-sac-input"
                          id="field-1"
                          maxLength={26}
                          pattern="[a-zA-Z]+"
                         placeholder={t("enterOtherCaseType")}
                          value={customCaseTypeName}
                          onChange={(selectedCase: any) =>
                            setCustomCaseTypeName(selectedCase?.target?.value)
                          }
                        />
                      </>
                    </div>
                  )}
                </div>
              )}
            </div>
            {!isEMTUser && <hr />}
            {!isEMTUser && (
              <div className="search-input-container">
                {/* Search with first and last name,dob section  */}
                <div className="search-with-first-last-name-dob-container">
                  <IonGrid>
                    <IonRow>
                  
                        <span className="sub-title">{t("epicSearch")}</span>
  
                    </IonRow>
                    <IonRow>
                      {/* first name section */}
                      <IonCol
                        sizeXs="5"
                        sizeSm="5"
                        sizeMd="5"
                        sizeXl="3"
                        sizeLg="3"
                      >
                        <div className="patient-details-form-input-container">
                        <p>{t("firstName")}</p>
                          <div
                            className={`input-item ${
                              isError && !firstName && "error-border"
                            }`}
                          >
                            <IonInput
                              maxlength={30}
                             placeholder={t("firstName")}
                              value={firstName}
                              onIonChange={(e: any) =>
                                setFirstName(e.target.value)
                              }
                              onKeyPress={handleKeyPress}
                            ></IonInput>
                          </div>
                          {isError && !firstName && (
                            <span className="error-text">
                              {t("pleaseEnterFirstName")}
                            </span>
                          )}
                        </div>
                      </IonCol>
                      {/* last name section */}
                      <IonCol
                        sizeXs="5"
                        sizeSm="5"
                        sizeMd="5"
                        sizeXl="3"
                        sizeLg="3"
                        style={{ margin: "0 0 0 .5rem" }}
                      >
                        <div className="patient-details-form-input-container">
                          <p>{t("lastName")}</p>
                          <div
                            className={`input-item ${
                              isError && !lastName && "error-border"
                            }`}
                          >
                            <IonInput
                              maxlength={30}
                                placeholder={t("lastName")}
                              value={lastName}
                              onIonChange={(e: any) =>
                                setLastName(e.target.value)
                              }
                              onKeyPress={handleKeyPress}
                            ></IonInput>
                          </div>
                          {isError && !lastName && (
                            <span className="error-text">
                            {t("enterLastName")}
                            </span>
                          )}
                        </div>
                      </IonCol>
                      {/* dob section */}
                      <IonCol
                        sizeXs="7"
                        sizeSm="7"
                        sizeMd="5"
                        sizeXl="3"
                        sizeLg="3"
                        className="search-dob-col"
                      >
                        <div className="patient-details-form-input-container">
                        <p>{t("dateOfBirth")}</p>
                          <div
                            className={`input-item ${
                              isError && !dob && "error-border"
                            } `}
                          >
                            <div className="date-input-container">
                              <IonInput
                                placeholder="MM-DD-YYYY"
                                onIonChange={(e: any) => {
                                  if (e?.target?.value?.length === 10) {
                                    const formatedDate = dayjs(
                                      e?.target?.value,
                                      "MM-DD-YYYY"
                                    ).format("YYYY-MM-DD");
                                    setSelectedSearchDOB(formatedDate);
                                    setDob(formatedDate);
                                  }
                                }}
                                className={`date-input  search-date-input`}
                                value={
                                  dob
                                    ? dayjs(dob, "YYYY-MM-DD").format(
                                        "MM-DD-YYYY"
                                      )
                                    : ""
                                }
                              ></IonInput>
                              <IonButton
                                className={`date-picker-btn ${
                                  isError && !dob && "error-border"
                                } seacrh-date-btn`}
                                onClick={() => setIsCalendarModalOpen(true)}
                              >
                                <IonIcon slot="end" icon={calendar}></IonIcon>
                              </IonButton>
                            </div>
                            {isError && !dob && (
                              <span className="error-text">
                                Please select date of birth
                              </span>
                            )}
                            <IonModal
                              isOpen={isCalendarModalOpen}
                              className="time-picker-modal"
                              backdropDismiss={true}
                              onIonModalDidDismiss={() =>
                                setIsCalendarModalOpen(false)
                              }
                            >
                              <DatePickerComponent
                                dateFromParent={selectedSearchDOB}
                                isFromEMS={true}
                                isDateShort={false}
                                isDOB={true}
                                getSelectedDate={(value: string) => {
                                  setSelectedSearchDOB(value);
                                  setDob(value);
                                }}
                              ></DatePickerComponent>
                              <div className="reminder-button-container">
                                <div className="send-button-div">
                                  <IonButton
                                    className="submit-btn button primary-btn btn-curved"
                                    onClick={() => {
                                      setIsCalendarModalOpen(false);
                                    }}
                                  >
                                    Add
                                  </IonButton>
                                </div>
                              </div>
                            </IonModal>
                          </div>
                        </div>
                      </IonCol>
                      {/* search button section */}
                      <IonCol
                        sizeXs="2"
                        sizeSm="2"
                        sizeMd="3"
                        sizeXl="2"
                        sizeLg="2"
                        style={{ margin: "0 0 0 .5rem", alignSelf: "flex-end" }}
                      >
                        <div className="search-and-drop-container">
                          <div className="input-item">
                            <IonButton
                              className="btn patient-search-btn"
                              onClick={() => handlePatientSearch(true)}
                            >
                              Search
                            </IonButton>
                          </div>
                        </div>
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </div>
                <div
                  className="or-container align-center"
                  style={{ margin: "1rem 0 0.5rem 0" }}
                >
                 <p>{t("or")}</p>
                </div>
                {/* Search with id section */}
                <div className="search-with-mr-id-container  align-center">
                  <IonGrid>
                    <IonRow className="align-center">
                      {/* MRN Section */}
                     <p>{t("mrn")}</p>
                      <IonCol
                        sizeSm="5"
                        sizeMd="5"
                        sizeXl="4"
                        sizeLg="4"
                        style={{ margin: "0 0 0 .5rem" }}
                      >
                        <div
                          className={`input-item ${
                            isErrorID && !searchID && "error-border"
                          }`}
                        >
                          <IonInput
                            maxlength={30}
                            placeholder="MRN"
                            value={searchID}
                            onIonChange={(e: any) =>
                              setSearchID(e.target.value)
                            }
                            onKeyPress={(e: any) => {
                              const charCode = e.charCode;
                              // Check if the character is space
                              if (charCode === 32) {
                                e.preventDefault();
                              }
                            }}
                          ></IonInput>
                        </div>
                        {isErrorID && !searchID && (
                          <span className="error-text">Please enter id</span>
                        )}
                      </IonCol>
                      {/* Search button section */}
                      <IonCol sizeSm="2" sizeMd="2" sizeXl="2" sizeLg="2">
                        <div className="input-item">
                          <IonButton
                            style={{ borderRadius: "4px !important" }}
                            className="btn patient-search-btn br-1"
                            onClick={handlePatientSearchWithID}
                          >
                            <span>{t("search")}</span>
                          </IonButton>
                        </div>
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </div>
              </div>
            )}
            <div className="patient-details-header-container">
             <p>{t("patientDetails")}</p>
              {(resultPatientsList.length > 0 || isResultEmpty) && (
                <div>
                  <IonButton
                    className="show-hide-result-btn"
                    onClick={() => {
                      setIsShowResult(!isShowResult);
                    }}
                  >
                    {!isShowResult ? "Show Result" : "Hide Result"}
                  </IonButton>
                </div>
              )}
            </div>
            <hr />
            <div
              style={
                isLoading
                  ? {
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "10rem",
                    }
                  : {}
              }
            >
              {!isLoading ? (
                !isShowResult ? (
                  // Patient details section
                  <div className="patient-details-form-container">
                    <IonGrid>
                      <IonRow>
                        {/* First name section */}
                        <IonCol sizeXl="6" sizeLg="6">
                          <div className="patient-details-form-input-container">
                            <p>
                              <span className="required-symbol">*</span><p>{t("firstName")}</p>{" "}
                            </p>
                            <div className="input-item">
                              <IonInput
                                maxlength={30}
                                placeholder={t("firstName")}
                                onIonChange={(e: any) =>
                                  setFormData({
                                    ...formData,
                                    firstName: e.target.value,
                                  })
                                }
                                onKeyPress={handleKeyPress}
                                value={
                                  formData?.firstName ? formData?.firstName : ""
                                }
                                disabled={isEHIRAccount()}
                              ></IonInput>
                            </div>
                          </div>
                        </IonCol>
                        {/* Middle name section */}
                        <IonCol sizeXl="6" sizeLg="6">
                          <div className="patient-details-form-input-container">
                         <p>{t("middleName")}</p>
                            <div className="input-item">
                              <IonInput
                                maxlength={30}
                                placeholder={t("middleName")}
                                onIonChange={(e: any) =>
                                  setFormData({
                                    ...formData,
                                    middleName: e.target.value ?? "",
                                  })
                                }
                                onKeyPress={handleKeyPress}
                                value={formData?.middleName ?? ""}
                                disabled={isEHIRAccount()}
                              ></IonInput>
                            </div>
                          </div>
                        </IonCol>
                      </IonRow>
                      <IonRow>
                        {/* last name section */}
                        <IonCol sizeXl="6" sizeLg="6">
                          <div className="patient-details-form-input-container">
                          <p>{t("lastName")}</p>
                            <div className="input-item">
                              <IonInput
                                maxlength={30}
                                placeholder="Last Name"
                                onIonChange={(e: any) =>
                                  setFormData({
                                    ...formData,
                                    lastName: e.target.value ?? "",
                                  })
                                }
                                onKeyPress={handleKeyPress}
                                value={formData?.lastName ?? ""}
                                disabled={isEHIRAccount()}
                              ></IonInput>
                            </div>
                          </div>
                        </IonCol>
                        {/* Date of birth section */}
                        <IonCol sizeXl="6" sizeLg="6">
                          <div className="patient-details-form-input-container">
                            <p style={{ margin: "0 !important" }}>
                              <span className="required-symbol">*</span>Date of
                              Birth
                            </p>
                            <div className="input-item">
                              <div className="date-input-container">
                                <IonInput
                                  placeholder="MM-DD-YYYY"
                                  onIonChange={(e: any) => {
                                    if (e?.target?.value?.length === 10) {
                                      const formatedDate = dayjs(
                                        e?.target?.value,
                                        "MM-DD-YYYY"
                                      ).format("YYYY-MM-DD");

                                      setFormData({
                                        ...formData,
                                        dob: formatedDate,
                                      });
                                    } else {
                                      setFormData({
                                        ...formData,
                                        dob: e?.target?.value ?? "",
                                      });
                                    }
                                  }}
                                  value={
                                    formData.dob
                                      ? formData.dob.length === 10
                                        ? dayjs(
                                            formData.dob,
                                            "YYYY-MM-DD"
                                          ).format("MM-DD-YYYY")
                                        : formData.dob
                                      : ""
                                  }
                                  className="date-input"
                                  disabled={isEHIRAccount()}
                                  onKeyPress={(e: any) => {
                                    const charCode = e.charCode;
                                    // Check if the character is space
                                    if (charCode === 32) {
                                      e.preventDefault();
                                    }
                                  }}
                                ></IonInput>
                                <IonButton
                                  className="date-picker-btn"
                                  onClick={() =>
                                    setIsFormCalendarModalOpen(true)
                                  }
                                  disabled={isEHIRAccount()}
                                >
                                  <IonIcon slot="end" icon={calendar}></IonIcon>
                                </IonButton>
                              </div>
                              <IonModal
                                isOpen={isFormCalendarModalOpen}
                                className="time-picker-modal"
                                backdropDismiss={true}
                                onIonModalDidDismiss={() =>
                                  setIsFormCalendarModalOpen(false)
                                }
                              >
                                <DatePickerComponent
                                  dateFromParent={formData.dob}
                                  isFromEMS={true}
                                  isDateShort={false}
                                  isDOB={true}
                                  getSelectedDate={(value: string) => {
                                    setFormData({
                                      ...formData,
                                      dob: value,
                                    });
                                  }}
                                ></DatePickerComponent>
                                <div className="reminder-button-container">
                                  <div className="send-button-div">
                                    <IonButton
                                      className="submit-btn button primary-btn btn-curved"
                                      onClick={() => {
                                        setIsFormCalendarModalOpen(false);
                                      }}
                                    >
                                      {t("add")}
                                    </IonButton>
                                  </div>
                                </div>
                              </IonModal>
                            </div>
                          </div>
                        </IonCol>
                      </IonRow>
                      <IonRow>
                        {/* ID section */}
                        {!isEMTUser && (
                          <IonCol sizeXl="6" sizeLg="6">
                            <div className="patient-details-form-input-container">
                              <p>
                                <span className="required-symbol">*</span>MRN
                              </p>
                              <div className="input-item">
                                <IonInput
                                  maxlength={30}
                                  placeholder="MRN"
                                  onIonChange={(e: any) =>
                                    setFormData({
                                      ...formData,
                                      mrn: e.target.value,
                                    })
                                  }
                                  value={formData?.mrn ?? ""}
                                  onKeyPress={(e: any) => {
                                    const charCode = e.charCode;
                                    // Check if the character is space
                                    if (charCode === 32) {
                                      e.preventDefault();
                                    }
                                  }}
                                ></IonInput>
                              </div>
                            </div>
                          </IonCol>
                        )}
                        {/* Gender section */}
                        <IonCol sizeXl="6" sizeLg="6">
                          <div className="patient-details-form-input-container">
                            <p>
                              <span className="required-symbol">*</span>Gender
                            </p>
                            <div className="input-item">
                              <IonList>
                                <IonItem>
                                  <IonSelect
                                    aria-label="Fruit"
                                    interface="popover"
                                    placeholder="Select Gender"
                                    onIonChange={(e: any) => {
                                      setFormData({
                                        ...formData,
                                        gender: e.target.value,
                                      });
                                    }}
                                    value={formData.gender ?? ""}
                                    disabled={isEHIRAccount()}
                                  >
                                   <IonSelectOption value="Male">{t("male")}</IonSelectOption>
                  <IonSelectOption value="Female">{t("female")}</IonSelectOption>
                  <IonSelectOption value="Other">{t("other")}</IonSelectOption>
                                  </IonSelect>
                                </IonItem>
                              </IonList>
                            </div>
                          </div>
                        </IonCol>
                      </IonRow>
                    </IonGrid>
                  </div>
                ) : (
                  // Patient search result list section
                  <div className="patient-search-result-container">
                    {resultPatientsList.length > 0 ? (
                      <IonList>
                        {/* Search result list header */}
                        <IonItem className="patient-search-result-header">
                          <IonLabel className="name-label-list">
  {t("name")}
</IonLabel>

<IonLabel>
  {t("mrnEhrId")}
</IonLabel>

<IonLabel className="dob-label-list">
  {t("dateOfBirth")}
</IonLabel>

<IonLabel className="gender-label-list">
  {t("gender")}
</IonLabel>
                        </IonItem>
                        {/* Search result list items */}
                        <div className="patient-search-result-items-container">
                          {resultPatientsList?.map((patient: any) => {
                            return (
                              <IonItem
                                className="patient-search-result-item"
                                onClick={() => {
                                  handleResultItemClick(patient);
                                }}
                              >
                                <IonLabel className="name-label-list">{`${patient?.first_name} ${patient?.middle_name} ${patient?.last_name}`}</IonLabel>
                                <IonLabel>
                                  {patient?.mr_number
                                    ? patient?.mr_number
                                    : patient?.ehr_id}
                                </IonLabel>
                                <IonLabel className="dob-label-list ml-1">
                                  {patient?.dob &&
                                    dayjs(patient?.dob, "YYYY-MM-DD").format(
                                      "MM-DD-YYYY"
                                    )}
                                </IonLabel>
                                <IonLabel className="gender-label-list ml-1">
                                  {patient?.gender}
                                </IonLabel>
                              </IonItem>
                            );
                          })}
                        </div>
                      </IonList>
                    ) : (
                      <div className="no-results-div">
                        <div className="input-icon  icon-no_result-div">
                          <IonIcon icon={peopleOutline}></IonIcon>
                        </div>
                        <div className="no-results-message">
                         {t("noPatientsFound")}
                        </div>
                      </div>
                    )}
                  </div>
                )
              ) : (
                <div className="loader"></div>
              )}
            </div>
          </div>
          {/* Buttons section */}
          {!isShowResult && !isLoading && (
            <div className="add-edit-patient-btn-cont">
              <IonButton
                className="add-edit-patient-send-btn bg-grey"
                onClick={() => {
                  setFormData({
                    ...formData,
                    firstName: "",
                    middleName: "",
                    lastName: "",
                    dob: "",
                    mrn: "",
                    gender: "",
                  });
                  setFetchedResultData({});
                  setIsEPICSearch(false);
                }}
                disabled={isDataEmpty()}
              >
                {t("reset")}
              </IonButton>
              {/* Save button section */}
              <IonButton
                className="btn add-edit-patient-send-btn"
                onClick={() => {
                  handleSaveButton({
                    fetchedResultData,
                    formData,
                    otherCaseType:
                      customCaseTypeName &&
                      selectedProvidersNames.find(
                        (name: string) => name === "Other"
                      )
                        ? customCaseTypeName
                        : "",
                  });
                  setIsEPICSearch(false);
                }}
                disabled={isDataEmpty()}
              >
                <span> {t("save")}</span>
              </IonButton>
            </div>
          )}
        </div>
      </IonModal>
    </div>
  );
};
